import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/*
 * German legal pages (launch requirement for a .de shop).
 *
 * ⚠️ Placeholders in OWNER below must be filled with real business details
 * before launch. These texts follow common German-market structure but are
 * not legal advice — have them reviewed before going live.
 */
const OWNER = {
  name: "Christo Teresof",
  street: "Weißenböckstraße 6",
  city: "80993 München",
  country: "Deutschland",
  email: "aintdeady@gmail.com",
  vat: "Gemäß § 19 Abs. 1 UStG (Kleinunternehmerregelung) wird keine Umsatzsteuer berechnet und ausgewiesen.",
};

const LegalLayout = ({ title, children }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F5]">
      <header className="border-b border-[#2A2A2A] py-4 px-4 sm:px-6 sticky top-0 bg-[#050505]/95 backdrop-blur z-40">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-anton text-lg neon-text no-underline">AIN'T DEAD YET</Link>
          <Link to="/" className="flex items-center gap-2 font-jet text-xs text-[#A1A1AA] hover:text-[#39FF14] transition-colors no-underline">
            <ArrowLeft size={14} /> Zurück
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-14 legal-content">
        <h1 className="font-anton text-3xl sm:text-4xl uppercase tracking-tight mb-10">{title}</h1>
        {children}
      </main>
      <footer className="border-t border-[#2A2A2A] py-8 text-center font-jet text-[10px] text-[#52525B]">
        © {new Date().getFullYear()} aintdeadyet.de —{" "}
        <Link to="/impressum" className="hover:text-[#39FF14]">Impressum</Link> ·{" "}
        <Link to="/datenschutz" className="hover:text-[#39FF14]">Datenschutz</Link> ·{" "}
        <Link to="/agb" className="hover:text-[#39FF14]">AGB</Link> ·{" "}
        <Link to="/widerruf" className="hover:text-[#39FF14]">Widerruf</Link>
      </footer>
    </div>
  );
};

const H = ({ children }) => <h2 className="font-bebas text-xl tracking-[0.15em] text-[#39FF14] mt-10 mb-3 uppercase">{children}</h2>;
const P = ({ children }) => <p className="text-sm text-[#A1A1AA] leading-relaxed mb-3">{children}</p>;

export const Impressum = () => (
  <LegalLayout title="Impressum">
    <H>Angaben gemäß § 5 DDG</H>
    <P>{OWNER.name}<br />{OWNER.street}<br />{OWNER.city}<br />{OWNER.country}</P>
    <H>Kontakt</H>
    <P>E-Mail: {OWNER.email}</P>
    <H>Umsatzsteuer</H>
    <P>{OWNER.vat}</P>
    <H>Verantwortlich für den Inhalt</H>
    <P>{OWNER.name}, Anschrift wie oben.</P>
    <H>EU-Streitschlichtung</H>
    <P>
      Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
      <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-[#39FF14]">
        https://ec.europa.eu/consumers/odr/
      </a>
      . Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer
      Verbraucherschlichtungsstelle teilzunehmen.
    </P>
  </LegalLayout>
);

export const Datenschutz = () => (
  <LegalLayout title="Datenschutzerklärung">
    <H>1. Verantwortlicher</H>
    <P>{OWNER.name}, {OWNER.street}, {OWNER.city}, {OWNER.country} — E-Mail: {OWNER.email}</P>

    <H>2. Hosting und Server-Logfiles</H>
    <P>
      Beim Aufruf dieser Website verarbeitet unser Server automatisch technische Daten (IP-Adresse,
      Datum und Uhrzeit, aufgerufene Seite, Browsertyp), um die Website sicher bereitzustellen.
      Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einem stabilen und
      sicheren Betrieb). Die Logdaten werden nach kurzer Zeit gelöscht.
    </P>

    <H>3. Bestellung und Zahlungsabwicklung</H>
    <P>
      Zur Abwicklung Ihres Kaufs verarbeiten wir Ihre E-Mail-Adresse, das gekaufte Produkt, den
      Zahlungsstatus und Ihren persönlichen VIP-Code. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO
      (Vertragserfüllung). Steuer- und handelsrechtliche Aufbewahrungspflichten bleiben unberührt.
    </P>
    <P>
      <strong className="text-[#F5F5F5]">Stripe:</strong> Bei Kartenzahlung erfolgt die Zahlungsabwicklung durch Stripe Payments
      Europe, Ltd., 1 Grand Canal Street Lower, Dublin, Irland. Ihre Zahlungsdaten werden direkt von
      Stripe erhoben; wir erhalten keine vollständigen Kartendaten. Weitere Informationen:{" "}
      <a href="https://stripe.com/de/privacy" target="_blank" rel="noopener noreferrer" className="text-[#39FF14]">stripe.com/de/privacy</a>
    </P>
    <P>
      <strong className="text-[#F5F5F5]">PayPal:</strong> Bei Zahlung über PayPal erfolgt die Abwicklung durch PayPal (Europe)
      S.à r.l. et Cie, S.C.A., 22-24 Boulevard Royal, 2449 Luxemburg. Weitere Informationen:{" "}
      <a href="https://www.paypal.com/de/legalhub/privacy-full" target="_blank" rel="noopener noreferrer" className="text-[#39FF14]">paypal.com/de/legalhub/privacy-full</a>
    </P>
    <P>
      <strong className="text-[#F5F5F5]">Resend (E-Mail-Versand):</strong> Für den Versand der Kaufbestätigung nutzen wir den
      Dienst Resend (Plus Five Five, Inc., USA). Dabei wird Ihre E-Mail-Adresse in die USA übermittelt;
      die Übermittlung wird durch EU-Standardvertragsklauseln abgesichert. Weitere Informationen:{" "}
      <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#39FF14]">resend.com/legal/privacy-policy</a>
    </P>

    <H>4. Coaching-Intake und Gesundheitsdaten</H>
    <P>
      Wenn Sie eine 1-on-1 Online-Consultation gebucht haben, erheben wir über das Intake-Formular
      Angaben zu Ihrer Person und Ihrem Training (u.&nbsp;a. Alter, Körpergröße, Körpergewicht,
      Trainingserfahrung, Zielsetzung, Ernährungseinschränkungen, Zeitzone und Wunschtermin) sowie
      Angaben zu <strong className="text-[#F5F5F5]">Verletzungen und Vorerkrankungen</strong>.
    </P>
    <P>
      Angaben zu Verletzungen, Vorerkrankungen und Körpermaßen sind Gesundheitsdaten und damit
      besondere Kategorien personenbezogener Daten im Sinne des Art. 9 DSGVO. Wir verarbeiten diese
      Daten ausschließlich auf Grundlage Ihrer ausdrücklichen Einwilligung gemäß
      <strong className="text-[#F5F5F5]"> Art. 9 Abs. 2 lit. a DSGVO</strong>, die Sie im
      Intake-Formular über eine gesonderte Checkbox erteilen. Sie können diese Einwilligung
      jederzeit mit Wirkung für die Zukunft widerrufen (E-Mail an {OWNER.email}); die Rechtmäßigkeit
      der bis dahin erfolgten Verarbeitung bleibt unberührt. Ohne diese Einwilligung können wir das
      Coaching nicht sicher durchführen.
    </P>
    <P>
      <strong className="text-[#F5F5F5]">Zweck:</strong> ausschließlich die Planung und Durchführung
      Ihres Coachings (Trainings- und Ernährungsplanung, Rücksichtnahme auf Verletzungen,
      Terminabstimmung). Eine Weitergabe an Dritte, eine Nutzung zu Werbezwecken und eine
      automatisierte Entscheidungsfindung finden nicht statt.
    </P>
    <P>
      <strong className="text-[#F5F5F5]">Speicherdauer und automatische Löschung:</strong> Ihre
      Intake-Angaben werden auf unserem eigenen Server in Deutschland gespeichert und
      <strong className="text-[#F5F5F5]"> automatisiert gelöscht</strong>, sobald die
      Aufbewahrungsfrist nach Ende Ihres Coaching-Blocks abgelaufen ist (standardmäßig 180 Tage nach
      Blockende). Die Löschung erfolgt systemseitig und erfasst ausschließlich die
      Gesundheits- und Intake-Angaben. Die reinen Bestelldaten (Kaufdatum, Betrag, E-Mail-Adresse)
      bleiben davon unberührt, da für sie gesetzliche Aufbewahrungspflichten gelten
      (u.&nbsp;a. § 147 AO). Auf Wunsch löschen wir Ihre Intake-Angaben auch vorzeitig.
    </P>
    <P>
      <strong className="text-[#F5F5F5]">Discord:</strong> Die laufende Betreuung und die beiden
      Video-Calls finden über Discord statt (Discord Netherlands BV / Discord Inc.). Wenn Sie dem
      Discord-Server beitreten, gelten zusätzlich die Datenschutzbestimmungen von Discord. Bitte
      teilen Sie uns Gesundheitsdaten nur über das Intake-Formular mit, nicht über Discord-Nachrichten.
    </P>

    <H>5. Kontaktformular</H>
    <P>
      Wenn Sie uns über das Kontaktformular schreiben, speichern wir Name, E-Mail-Adresse und Nachricht
      zur Bearbeitung Ihrer Anfrage (Art. 6 Abs. 1 lit. b bzw. lit. f DSGVO). Die Daten werden gelöscht,
      sobald sie für die Bearbeitung nicht mehr erforderlich sind.
    </P>

    <H>6. Webanalyse (PostHog) — nur mit Einwilligung</H>
    <P>
      Nur wenn Sie über den Cookie-Hinweis einwilligen (Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1 TDDDG),
      setzen wir PostHog (PostHog, Inc., USA) zur Analyse der Website-Nutzung ein. Dabei können
      Nutzungsdaten und Sitzungsaufzeichnungen verarbeitet und in die USA übermittelt werden. Ihre
      Einwilligung können Sie jederzeit mit Wirkung für die Zukunft widerrufen, indem Sie die im
      Browser gespeicherte Auswahl löschen. Ohne Einwilligung wird kein Analyse-Tracking geladen.
    </P>

    <H>7. Discord-VIP-Zugang</H>
    <P>
      Der Beitritt zu unserem Discord-Server über den Einladungslink ist freiwillig. Es gelten die
      Datenschutzbestimmungen von Discord Inc.
    </P>

    <H>8. Ihre Rechte</H>
    <P>
      Sie haben das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art. 17),
      Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) und Widerspruch gegen
      Verarbeitungen auf Grundlage berechtigter Interessen (Art. 21). Außerdem können Sie sich bei
      einer Datenschutz-Aufsichtsbehörde beschweren (Art. 77 DSGVO).
    </P>
  </LegalLayout>
);

export const AGB = () => (
  <LegalLayout title="Allgemeine Geschäftsbedingungen (AGB)">
    <H>1. Geltungsbereich</H>
    <P>
      Diese AGB gelten für alle Bestellungen über die Website aintdeadyet.de zwischen {OWNER.name}
      {" "}(nachfolgend „Anbieter") und Verbrauchern (nachfolgend „Kunde").
    </P>
    <H>2. Vertragsschluss</H>
    <P>
      Die Produktdarstellung auf der Website ist kein bindendes Angebot. Mit Abschluss des
      Bezahlvorgangs (Stripe oder PayPal) gibt der Kunde ein verbindliches Angebot ab. Der Vertrag
      kommt mit der Zahlungsbestätigung und dem Versand der Bestellbestätigung per E-Mail zustande.
    </P>
    <H>3. Preise und Zahlung</H>
    <P>
      Alle Preise sind Endpreise in Euro. Gemäß § 19 Abs. 1 UStG (Kleinunternehmerregelung) wird
      keine Umsatzsteuer berechnet und ausgewiesen. Die Zahlung erfolgt per Kreditkarte (Stripe)
      oder PayPal. Die Bereitstellung des Produkts erfolgt unmittelbar nach erfolgreicher Zahlung.
    </P>
    <H>4. Digitale Inhalte / Lieferung</H>
    <P>
      Kaufgegenstand sind digitale Inhalte (z.&nbsp;B. Trainingsprogramm als Download-Datei). Nach
      Zahlungseingang erhält der Kunde den Download-Zugang auf der Bestätigungsseite sowie per E-Mail
      inklusive persönlichem VIP-Code für den Discord-Zugang. Eine physische Lieferung erfolgt nicht.
    </P>
    <H>5. Coaching-Leistungen (1-on-1 Online-Consultation)</H>
    <P>
      <strong className="text-[#F5F5F5]">Leistungsumfang:</strong> Das Coaching wird als
      zusammenhängender Block von vier Wochen erbracht und umfasst einen Kickoff-Call von
      45&nbsp;Minuten, einen Abschluss-Call von 45&nbsp;Minuten in Woche&nbsp;4 sowie in der Zeit
      dazwischen eine asynchrone Betreuung (Trainingsplanung, Ernährungsrichtwerte, Anpassungen,
      Video-Feedback zur Technik) über einen privaten Kanal auf dem Discord-Server des Anbieters.
      Der Block beginnt mit dem Zahlungseingang und endet 28&nbsp;Tage danach.
    </P>
    <P>
      <strong className="text-[#F5F5F5]">Termine:</strong> Die beiden Calls werden individuell
      abgestimmt und über Discord durchgeführt. Eine Absage oder Verschiebung ist bis
      24&nbsp;Stunden vor dem vereinbarten Termin kostenfrei möglich. Ohne rechtzeitige Absage
      versäumte Termine gelten als erbracht. Anfragen im asynchronen Teil werden in der Regel
      innerhalb von 24&nbsp;Stunden an Werktagen beantwortet.
    </P>
    <P>
      <strong className="text-[#F5F5F5]">Begrenzte Plätze:</strong> Der Anbieter betreut nur eine
      begrenzte Anzahl von Kunden gleichzeitig. Ist die verfügbare Anzahl erreicht, ist eine Buchung
      technisch nicht möglich; die auf der Website angezeigte Anzahl freier Plätze entspricht dem
      tatsächlichen Bestand.
    </P>
    <P>
      <strong className="text-[#F5F5F5]">Keine medizinische Beratung:</strong> Das Coaching ist ein
      Trainings- und Ernährungscoaching. Es stellt <strong className="text-[#F5F5F5]">keine
      medizinische, therapeutische, psychotherapeutische oder ernährungsmedizinische Beratung</strong>{" "}
      dar; es erfolgen keine Diagnosen und keine Behandlung von Krankheiten. Der Anbieter ist kein
      Arzt und keine Ernährungsfachkraft im Sinne der Heilberufe.
    </P>
    <P>
      <strong className="text-[#F5F5F5]">Mitwirkungspflicht des Kunden:</strong> Der Kunde ist
      verpflichtet, im Intake-Formular wahrheitsgemäße Angaben zu machen und relevante
      Vorerkrankungen, Verletzungen, Beschwerden und Medikamente anzugeben sowie Änderungen
      unverzüglich mitzuteilen. Vor Trainingsbeginn wird eine ärztliche Abklärung empfohlen. Das
      Training erfolgt auf eigene Verantwortung.
    </P>
    <P>
      <strong className="text-[#F5F5F5]">Kein Erfolgsversprechen:</strong> Ein bestimmter Trainings-,
      Körper- oder Gesundheitserfolg wird nicht geschuldet und nicht zugesichert. Der Erfolg hängt
      maßgeblich von der Mitwirkung des Kunden ab.
    </P>
    <P>
      <strong className="text-[#F5F5F5]">Mindestalter:</strong> Das Coaching richtet sich
      ausschließlich an volljährige Personen (18&nbsp;Jahre und älter).
    </P>
    <P>
      <strong className="text-[#F5F5F5]">Ablehnung und Beendigung:</strong> Der Anbieter kann eine
      Betreuung ablehnen oder aus wichtigem Grund beenden, insbesondere wenn eine ärztliche
      Betreuung angezeigt ist, Angaben unvollständig oder unrichtig sind oder eine vertrauensvolle
      Zusammenarbeit nicht möglich ist. Bereits gezahlte Beträge werden in diesem Fall anteilig für
      die noch nicht erbrachte Leistung erstattet.
    </P>
    <H>6. Widerrufsrecht</H>
    <P>
      Verbrauchern steht grundsätzlich ein 14-tägiges Widerrufsrecht zu. Bei digitalen Inhalten
      erlischt das Widerrufsrecht, wenn der Kunde ausdrücklich zugestimmt hat, dass mit der Ausführung
      des Vertrags vor Ablauf der Widerrufsfrist begonnen wird, und seine Kenntnis vom Erlöschen des
      Widerrufsrechts bestätigt hat (§ 356 Abs. 5 BGB).
    </P>
    <P>
      Bei <strong className="text-[#F5F5F5]">Dienstleistungen</strong> (Coaching) erlischt das
      Widerrufsrecht abweichend erst mit der <strong className="text-[#F5F5F5]">vollständigen
      Erbringung</strong> der Leistung, sofern der Kunde ausdrücklich verlangt hat, dass vor Ablauf
      der Widerrufsfrist mit der Ausführung begonnen wird, und seine Kenntnis vom Verlust des
      Widerrufsrechts bei vollständiger Vertragserfüllung bestätigt hat (§ 356 Abs. 4 BGB). Widerruft
      der Kunde während der laufenden Betreuung, schuldet er anteiligen Wertersatz für die bereits
      erbrachten Leistungen (§ 357 Abs. 8 BGB).
    </P>
    <P>
      Die jeweilige Zustimmung wird im Bestellvorgang über eine Checkbox eingeholt. Einzelheiten:{" "}
      <Link to="/widerruf" className="text-[#39FF14]">Widerrufsbelehrung</Link>.
    </P>
    <H>7. Nutzungsrechte</H>
    <P>
      Der Kunde erhält ein einfaches, nicht übertragbares Nutzungsrecht für private Zwecke. Weitergabe,
      Weiterverkauf oder öffentliche Zugänglichmachung der Inhalte sind untersagt.
    </P>
    <H>8. Gesundheitshinweis</H>
    <P>
      Die Inhalte (Trainingsprogramme, Ernährungsempfehlungen, Rechner, Coaching) stellen keine
      medizinische Beratung dar und ersetzen keinen Arztbesuch. Vor Trainingsbeginn wird empfohlen,
      den Gesundheitszustand ärztlich abklären zu lassen — insbesondere bei Vorerkrankungen,
      Verletzungen, Schwangerschaft oder der Einnahme von Medikamenten. Das Training erfolgt auf
      eigene Verantwortung.
    </P>
    <H>9. VIP-Discord-Zugang</H>
    <P>
      Der VIP-Zugang zum Discord-Server ist eine freiwillige Zusatzleistung ohne gesonderten
      Vertragswert. Umfang und Verfügbarkeit können sich ändern; bei Verstößen gegen die Serverregeln
      kann der Zugang entzogen werden.
    </P>
    <H>10. Haftung</H>
    <P>
      Der Anbieter haftet unbeschränkt bei Vorsatz und grober Fahrlässigkeit sowie bei Verletzung von
      Leben, Körper und Gesundheit. Bei einfacher Fahrlässigkeit haftet der Anbieter nur für die
      Verletzung wesentlicher Vertragspflichten, begrenzt auf den vorhersehbaren, vertragstypischen
      Schaden.
    </P>
    <H>11. Schlussbestimmungen</H>
    <P>
      Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts; gesetzliche Verbraucherschutzvorschriften
      des Aufenthaltsstaats bleiben unberührt. Sollten einzelne Bestimmungen unwirksam sein, bleibt der
      Vertrag im Übrigen wirksam.
    </P>
  </LegalLayout>
);

export const Widerruf = () => (
  <LegalLayout title="Widerrufsbelehrung">
    <H>Widerrufsrecht</H>
    <P>
      Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.
      Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des Vertragsschlusses.
    </P>
    <P>
      Um Ihr Widerrufsrecht auszuüben, müssen Sie uns ({OWNER.name}, {OWNER.street}, {OWNER.city},
      E-Mail: {OWNER.email}) mittels einer eindeutigen Erklärung (z.&nbsp;B. per E-Mail) über Ihren
      Entschluss, diesen Vertrag zu widerrufen, informieren. Zur Wahrung der Widerrufsfrist reicht es
      aus, dass Sie die Mitteilung über die Ausübung des Widerrufsrechts vor Ablauf der Widerrufsfrist
      absenden.
    </P>
    <H>Folgen des Widerrufs</H>
    <P>
      Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten
      haben, unverzüglich und spätestens binnen vierzehn Tagen ab dem Tag zurückzuzahlen, an dem die
      Mitteilung über Ihren Widerruf bei uns eingegangen ist. Für die Rückzahlung verwenden wir dasselbe
      Zahlungsmittel, das Sie bei der ursprünglichen Transaktion eingesetzt haben.
    </P>
    <H>Vorzeitiges Erlöschen bei digitalen Inhalten</H>
    <P>
      Das Widerrufsrecht erlischt bei Verträgen über die Lieferung von nicht auf einem körperlichen
      Datenträger befindlichen digitalen Inhalten gemäß § 356 Abs. 5 BGB, wenn wir mit der Ausführung
      des Vertrags begonnen haben, nachdem Sie:
    </P>
    <P>
      1. ausdrücklich zugestimmt haben, dass wir mit der Ausführung des Vertrags (Bereitstellung des
      Downloads) vor Ablauf der Widerrufsfrist beginnen, und<br />
      2. Ihre Kenntnis davon bestätigt haben, dass Sie durch diese Zustimmung mit Beginn der Ausführung
      des Vertrags Ihr Widerrufsrecht verlieren.
    </P>
    <P>
      Diese Zustimmung erteilen Sie im Bestellvorgang über die entsprechende Checkbox vor der Zahlung.
    </P>
    <H>Vorzeitiges Erlöschen bei Dienstleistungen (Coaching)</H>
    <P>
      Bei Verträgen über die Erbringung von Dienstleistungen — hierzu zählt die 1-on-1
      Online-Consultation — erlischt das Widerrufsrecht gemäß § 356 Abs. 4 BGB erst dann, wenn wir
      die Dienstleistung <strong className="text-[#F5F5F5]">vollständig erbracht</strong> haben und
      mit der Ausführung erst begonnen haben, nachdem Sie:
    </P>
    <P>
      1. ausdrücklich verlangt haben, dass wir vor Ablauf der Widerrufsfrist mit der Ausführung
      beginnen, und<br />
      2. Ihre Kenntnis davon bestätigt haben, dass Sie Ihr Widerrufsrecht bei vollständiger
      Vertragserfüllung verlieren.
    </P>
    <P>
      Anders als bei digitalen Inhalten erlischt Ihr Widerrufsrecht bei Coaching-Leistungen also
      nicht bereits mit dem Kauf, sondern erst mit dem vollständigen Abschluss des Coaching-Blocks.
    </P>
    <H>Wertersatz bei Widerruf während der Betreuung</H>
    <P>
      Widerrufen Sie den Coaching-Vertrag, nachdem Sie ausdrücklich verlangt haben, dass wir vor
      Ablauf der Widerrufsfrist mit der Leistung beginnen, so schulden Sie uns gemäß § 357 Abs. 8
      BGB einen <strong className="text-[#F5F5F5]">anteiligen Betrag</strong> für die bis zum
      Zeitpunkt des Widerrufs bereits erbrachten Leistungen. Der Anteil bemisst sich am Gesamtumfang
      der vertraglich vereinbarten Leistung (vier Wochen Betreuung, Kickoff-Call und Abschluss-Call).
    </P>
    <H>Muster-Widerrufsformular</H>
    <P>
      Wenn Sie den Vertrag widerrufen wollen, können Sie folgende Angaben per E-Mail an {OWNER.email}{" "}
      senden: „Hiermit widerrufe ich den von mir abgeschlossenen Vertrag über den Kauf der folgenden
      Ware/Dienstleistung: … — Bestellt am: … — Name und Anschrift des Verbrauchers: … — Datum: …"
    </P>
  </LegalLayout>
);
